import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { BellHeaderProps } from "@companion-core/shared/app/Components/Bell/Header/index";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import {
  getPreviousStep,
  hasFormPath,
  hasOnlyOneRadioShape,
} from "@companion-core/shared/app/Utils/dynamicForm";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { getInitialBellContent } from "@companion-core/shared/app/Utils/bell";
import "@companion-core/web/src/Assets/Styles/scss/bell.scss";
import { GetPreviousTreeElementIndex } from "@companion-core/shared/app/Utils/wizardHelper";

export const BellHeader = ({ onClose }: BellHeaderProps) => {
  const isBellHomeEnabled: boolean = getModule("concierge")?.modules.bell.options.home.enabled;
  const { t } = useTranslation();
  const { bellContent, defaultBehaviour, clearBell, setBellContent, headerAccessibilityLabel } =
    useBellStore((state) => ({
      bellContent: state.bellContent,
      defaultBehaviour: state.defaultBehaviour,
      clearBell: state.clearBell,
      setBellContent: state.setBellContent,
      headerAccessibilityLabel: state.headerAccessibilityLabel,
    }));
  const {
    currentStep,
    currentForm,
    formTree,
    setCurrentStep,
    setCurrentForm,
    resetWizard,
    setFields,
    setDefaultApiValues,
  } = useWizardStore((state) => ({
    formTree: state.formTree,
    currentForm: state.currentForm,
    currentStep: state.currentStep,
    resetWizard: state.resetWizard,
    setCurrentStep: state.setCurrentStep,
    setCurrentForm: state.setCurrentForm,
    setFields: state.setFields,
    setDefaultApiValues: state.setDefaultApiValues,
  }));

  /**
   * This state is used to know if we can go back or not
   * We can go back if we are not in the "home" page or in the "categories" page when the "home" page is disabled
   */
  const canGoBack = useMemo(() => {
    const previousTreeElementIndex =
      currentStep && currentStep?.order && currentForm && currentForm?.jsonPath
        ? GetPreviousTreeElementIndex(formTree, currentStep.order, currentForm.jsonPath)
        : null;
    return (
      (bellContent === "categories" && isBellHomeEnabled) ||
      (bellContent === "form" &&
        (defaultBehaviour ||
          (currentStep?.order != null && currentStep.order > 1) ||
          previousTreeElementIndex != null))
    );
  }, [currentForm, currentStep, formTree, bellContent, isBellHomeEnabled]);

  /**
   * This function is used to reset the wizard and the bell and go to the "home" page
   */
  const resetAll = useCallback(() => {
    resetWizard();
    clearBell(getInitialBellContent());
  }, [resetWizard, clearBell]);

  /**
   * This function is used to go back to the previous step
   * - If we are in the "categories" page, we reset the wizard and the bell and go to the "home" page
   * - If we are in the first step of the form, we reset the wizard and the bell and go to the "categories" page
   * - If we are in the middle of the form, we go to the previous step
   */
  const handleBackButton = useCallback(() => {
    if (bellContent === "categories") {
      resetAll();
    } else if (currentForm && currentStep?.order) {
      const { step, jsonForm } = getPreviousStep(currentForm, currentStep.order, formTree);
      const hasFormPathInField = step?.fields.some((field) => hasFormPath(field));
      setCurrentForm(jsonForm);
      if (step && hasOnlyOneRadioShape(step)) step.fields[0].value = ""; // do not need to reload the previous value for radio shapes
      if (hasFormPathInField) {
        setFields([]);
        setDefaultApiValues({ ...jsonForm.defaultApiValues });
        setCurrentStep(step);
      } else if (step) {
        setCurrentStep(step);
      } else {
        resetAll();
        setBellContent("categories");
      }
    }
  }, [currentForm, currentStep, formTree, bellContent]);

  const handleOnClose = useMemo(() => {
    return () => {
      resetAll();
      onClose();
    };
  }, [onClose]);

  const homeTitleAriaLabel = useMemo(() => {
    const defaultTitle = t("request.concierge.header.title");
    const fieldWithRadioShape = currentStep?.fields.find((field) => field.type === "radioshape");
    const titleLabel =
      ["home", "categories"].includes(bellContent ?? "") || currentStep?.type === "summary"
        ? headerAccessibilityLabel !== ""
          ? headerAccessibilityLabel
          : defaultTitle
        : fieldWithRadioShape?.options?.length
          ? t("request.concierge.header.accessibility.title.sub-categories", {
              count: fieldWithRadioShape?.options?.length,
            })
          : currentStep?.fields?.length
            ? t("request.concierge.header.accessibility.title.steps", {
                count: currentStep?.fields.length,
              })
            : defaultTitle;
    return titleLabel;
  }, [bellContent, currentStep, headerAccessibilityLabel, t]);

  return (
    <div id="bell-dialog-label" data-testid="bell__header" className="bell__header">
      {canGoBack && (
        <button
          data-testid="bell__icon--previous"
          className="bell__header-icon bell__header-icon-previous"
          aria-label={t("request.concierge.header.accessibility.previous")}
          onClick={handleBackButton}>
          <FontIcon name="JP-Arrow-left-bold" size={24} />
        </button>
      )}
      <h1
        // Accessibility: this ID is used in another component to manage the focus
        id="bell-dialog-title"
        aria-label={homeTitleAriaLabel}
        className="bell__header-title title-7"
        tabIndex={-1}>
        {t("request.concierge.header.title")}
      </h1>
      <button
        data-testid="bell__icon--close"
        className="bell__header-icon bell__header-icon-close"
        aria-label={t("request.concierge.header.accessibility.close")}
        onClick={handleOnClose}>
        <FontIcon name="JP-Close-bold" size={24} />
      </button>
    </div>
  );
};
